import { useEffect, useMemo, useState } from 'react';
import BanquetHelpPanel from './BanquetHelpPanel';

function BanquetQuotation({ token, selectedProperty, selectedOutletId = '', showHelp = false, onOpenFaq, leadPrefill, onLeadPrefillConsumed }) {
  const chainId = String(selectedProperty?.chain_id || '').trim();
  const propertyId = String(selectedProperty?.id || '').trim();
  const outletId = String(selectedOutletId || '').trim();

  const headers = useMemo(
    () => ({
      Authorization: `Bearer ${token}`,
      'x-chain-id': chainId,
      'x-property-id': propertyId,
      ...(outletId ? { 'x-outlet-id': outletId } : {})
    }),
    [token, chainId, propertyId, outletId]
  );

  const [halls, setHalls] = useState([]);
  const [layouts, setLayouts] = useState([]);
  const [eventSlots, setEventSlots] = useState([]);
  const [serviceModes, setServiceModes] = useState([]);
  const [rows, setRows] = useState([]);
  const [selectedHallId, setSelectedHallId] = useState('');
  const [selectedLayoutId, setSelectedLayoutId] = useState('');
  const [selectedQuotationId, setSelectedQuotationId] = useState('');
  const [editingQuotationId, setEditingQuotationId] = useState('');
  const [quotationItems, setQuotationItems] = useState([]);
  const [editingItemId, setEditingItemId] = useState('');
  const [draftItems, setDraftItems] = useState([]);
  const [draftItemEditIndex, setDraftItemEditIndex] = useState(-1);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [form, setForm] = useState({
    customer_name: '',
    customer_phone: '',
    customer_email: '',
    expected_pax: '',
    slot_code: 'FULL_DAY',
    event_start_time: '',
    event_end_time: '',
    event_start_date: '',
    event_end_date: '',
    total_estimated: '',
    event_name: '',
    advance_required: ''
  });
  const [itemForm, setItemForm] = useState({
    item_type: 'SERVICE',
    description: '',
    amount: ''
  });
  const [draftItemForm, setDraftItemForm] = useState({
    item_type: 'SERVICE',
    description: '',
    amount: ''
  });
  const [linkedLeadId, setLinkedLeadId] = useState(null);

  const parseJson = async (res) => {
    const text = await res.text();
    try {
      const json = text ? JSON.parse(text) : {};
      return { data: json, error: json?.error || '' };
    } catch {
      return { data: null, error: text || '' };
    }
  };

  const todayLocal = (() => {
    const d = new Date();
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${y}-${m}-${day}`;
  })();

  const loadHalls = async () => {
    const res = await fetch('/api/v1/banquet/halls', { headers });
    const { data, error: apiError } = await parseJson(res);
    if (!res.ok) throw new Error(apiError || 'Failed to load halls');
    setHalls(Array.isArray(data) ? data : []);
  };

  const loadQuotations = async () => {
    const res = await fetch('/api/v1/banquet/quotations', { headers });
    const { data, error: apiError } = await parseJson(res);
    if (!res.ok) throw new Error(apiError || 'Failed to load quotations');
    const list = Array.isArray(data) ? data : [];
    setRows(list);
    if (!selectedQuotationId && list.length) setSelectedQuotationId(String(list[0].quotation_id));
    return list;
  };

  const loadEventSlots = async () => {
    const res = await fetch('/api/v1/banquet/event-slots', { headers });
    const { data, error: apiError } = await parseJson(res);
    if (!res.ok) throw new Error(apiError || 'Failed to load event slots');
    const list = Array.isArray(data) ? data : [];
    setEventSlots(list);
  };

  const loadServiceModes = async () => {
    const res = await fetch('/api/v1/banquet/service-modes', { headers });
    const { data, error: apiError } = await parseJson(res);
    if (!res.ok) throw new Error(apiError || 'Failed to load service modes');
    const list = Array.isArray(data) ? data : [];
    setServiceModes(list);
    const exists = list.some((m) => String(m.service_mode_code || '').toUpperCase() === String(itemForm.item_type || '').toUpperCase());
    if (list.length && !exists) {
      setItemForm((prev) => ({ ...prev, item_type: String(list[0].service_mode_code || 'SERVICE') }));
    }
    const draftExists = list.some((m) => String(m.service_mode_code || '').toUpperCase() === String(draftItemForm.item_type || '').toUpperCase());
    if (list.length && !draftExists) {
      setDraftItemForm((prev) => ({ ...prev, item_type: String(list[0].service_mode_code || 'SERVICE') }));
    }
  };

  const loadLayouts = async (hallId) => {
    if (!hallId) return setLayouts([]);
    const res = await fetch(`/api/v1/banquet/halls/${hallId}/layouts`, { headers });
    const { data, error: apiError } = await parseJson(res);
    if (!res.ok) throw new Error(apiError || 'Failed to load layouts');
    const list = Array.isArray(data) ? data : [];
    setLayouts(list);
    setSelectedLayoutId(list.length ? String(list[0].layout_id) : '');
  };

  const loadQuotationItems = async (quotationId) => {
    if (!quotationId) {
      setQuotationItems([]);
      return;
    }
    const res = await fetch(`/api/v1/banquet/quotations/${quotationId}/items`, { headers });
    const { data, error: apiError } = await parseJson(res);
    if (!res.ok) throw new Error(apiError || 'Failed to load quotation items');
    setQuotationItems(Array.isArray(data) ? data : []);
  };

  const reload = async () => {
    if (!chainId || !propertyId) return;
    setLoading(true);
    setError('');
    try {
      await Promise.all([loadHalls(), loadQuotations(), loadEventSlots(), loadServiceModes()]);
    } catch (err) {
      setError(err?.message || 'Failed to load banquet quotation data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!chainId || !propertyId) return;
    reload();
  }, [chainId, propertyId, outletId]);

  // Auto-populate form when navigating from Lead Detail
  useEffect(() => {
    if (!leadPrefill) return;
    if (leadPrefill.view_quotation_id) {
      // View existing quotation: select it in the list
      setSelectedQuotationId(String(leadPrefill.view_quotation_id));
      setEditingQuotationId('');
      loadQuotationItems(leadPrefill.view_quotation_id).catch(() => {});
    } else {
      // Create new quotation: pre-fill form from lead
      setSelectedQuotationId('');
      setEditingQuotationId('');
      setForm({
        customer_name: leadPrefill.customer_name || '',
        customer_phone: leadPrefill.customer_phone || '',
        customer_email: leadPrefill.customer_email || '',
        expected_pax: leadPrefill.expected_pax || '',
        slot_code: 'FULL_DAY',
        event_start_time: '',
        event_end_time: '',
        event_start_date: leadPrefill.event_start_date || '',
        event_end_date: leadPrefill.event_end_date || '',
        total_estimated: '',
        event_name: leadPrefill.event_name || '',
        advance_required: ''
      });
      setLinkedLeadId(leadPrefill.lead_id || null);
      setMessage(`Pre-filled from Lead ${leadPrefill.lead_no}. Add items and save the quotation.`);
    }
    if (onLeadPrefillConsumed) onLeadPrefillConsumed();
  }, [leadPrefill]);

  useEffect(() => {
    if (!selectedHallId) return;
    loadLayouts(selectedHallId).catch((err) => setError(err?.message || 'Failed to load layouts'));
  }, [selectedHallId]);

  useEffect(() => {
    const singleDay = Boolean(form.event_start_date) && Boolean(form.event_end_date) && String(form.event_start_date) === String(form.event_end_date);
    if (!singleDay && (form.slot_code !== 'FULL_DAY' || form.event_start_time || form.event_end_time)) {
      setForm((prev) => ({ ...prev, slot_code: 'FULL_DAY', event_start_time: '', event_end_time: '' }));
    }
  }, [form.event_start_date, form.event_end_date]);

  useEffect(() => {
    if (!selectedQuotationId) {
      setQuotationItems([]);
      setEditingItemId('');
      setItemForm({ item_type: serviceModes[0]?.service_mode_code || 'SERVICE', description: '', amount: '' });
      return;
    }
    loadQuotationItems(selectedQuotationId).catch((err) => setError(err?.message || 'Failed to load quotation items'));
  }, [selectedQuotationId]);

  const resetForm = () => {
    setEditingQuotationId('');
    setForm({
      customer_name: '',
      customer_phone: '',
      customer_email: '',
      expected_pax: '',
      slot_code: 'FULL_DAY',
      event_start_time: '',
      event_end_time: '',
      event_start_date: '',
      event_end_date: '',
      total_estimated: '',
      event_name: '',
      advance_required: ''
    });
    setDraftItems([]);
    setDraftItemEditIndex(-1);
    setDraftItemForm({ item_type: serviceModes[0]?.service_mode_code || 'SERVICE', description: '', amount: '' });
  };

  const resetItemForm = () => {
    setEditingItemId('');
    setItemForm({ item_type: serviceModes[0]?.service_mode_code || 'SERVICE', description: '', amount: '' });
  };

  const resetDraftItemForm = () => {
    setDraftItemEditIndex(-1);
    setDraftItemForm({ item_type: serviceModes[0]?.service_mode_code || 'SERVICE', description: '', amount: '' });
  };

  const saveQuotation = async () => {
    setMessage('');
    setError('');
    if (!form.event_start_date || !form.event_end_date) {
      setError('event_start_date and event_end_date are required');
      return;
    }
    try {
      const isEdit = Boolean(editingQuotationId);
      const calculatedTotal = Number(draftItemsTotal.toFixed(2));
      if (!isEdit && !draftItems.length) {
        setError('Add at least one quotation item line before creating quotation');
        return;
      }
      const payload = {
        customer_name: form.customer_name || null,
        customer_phone: form.customer_phone || null,
        customer_email: form.customer_email || null,
        expected_pax: form.expected_pax ? Number(form.expected_pax) : null,
        slot_code: form.slot_code || 'FULL_DAY',
        event_start_time: form.event_start_time || null,
        event_end_time: form.event_end_time || null,
        event_start_date: form.event_start_date,
        event_end_date: form.event_end_date,
        total_estimated: isEdit ? Number(form.total_estimated || 0) : calculatedTotal,
        ...(isEdit ? {} : { items: draftItems }),
        ...(linkedLeadId && !isEdit ? { lead_id: linkedLeadId } : {})
      };
      if (!Number.isFinite(Number(payload.total_estimated)) || Number(payload.total_estimated) <= 0) {
        setError('Add valid item amount lines so total estimated is greater than zero');
        return;
      }
      const res = await fetch(isEdit ? `/api/v1/banquet/quotations/${editingQuotationId}` : '/api/v1/banquet/quotations', {
        method: isEdit ? 'PUT' : 'POST',
        headers: { ...headers, 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });
      const { data, error: apiError } = await parseJson(res);
      if (!res.ok) throw new Error(apiError || (isEdit ? 'Failed to update quotation' : 'Failed to create quotation'));
      setMessage(isEdit ? `Quotation updated: ${data?.quotation_no || data?.quotation_id}` : `Quotation created: ${data?.quotation_no || data?.quotation_id}`);
      setSelectedQuotationId(String(data?.quotation_id || selectedQuotationId || ''));
      setLinkedLeadId(null);
      resetForm();
      await loadQuotations();
    } catch (err) {
      setError(err?.message || (editingQuotationId ? 'Failed to update quotation' : 'Failed to create quotation'));
    }
  };

  const editQuotation = (quotation) => {
    if (String(quotation?.status || '').toUpperCase() !== 'DRAFT') {
      setError('Only DRAFT quotations can be edited');
      return;
    }
    setEditingQuotationId(String(quotation.quotation_id));
    setDraftItems([]);
    setDraftItemEditIndex(-1);
    resetDraftItemForm();
    setSelectedQuotationId(String(quotation.quotation_id));
    setForm((prev) => ({
      ...prev,
      customer_name: quotation.customer_name || '',
      customer_phone: quotation.customer_phone || '',
      customer_email: quotation.customer_email || '',
      expected_pax: quotation.expected_pax ? String(quotation.expected_pax) : '',
      slot_code: quotation.slot_code || 'FULL_DAY',
      event_start_time: String(quotation.event_start_time || '').slice(0, 5),
      event_end_time: String(quotation.event_end_time || '').slice(0, 5),
      event_start_date: String(quotation.event_start_date || '').slice(0, 10),
      event_end_date: String(quotation.event_end_date || '').slice(0, 10),
      total_estimated: quotation.total_estimated ? String(quotation.total_estimated) : ''
    }));
  };

  const selectedQuotation = useMemo(
    () => rows.find((r) => String(r.quotation_id) === String(selectedQuotationId)) || null,
    [rows, selectedQuotationId]
  );

  const shareQuoteText = useMemo(() => {
    if (!selectedQuotation) return '';
    const lines = [
      `Banquet Quotation ${selectedQuotation.quotation_no}`,
      `Customer: ${selectedQuotation.customer_name || '-'}`,
      `Dates: ${String(selectedQuotation.event_start_date || '').slice(0, 10)} to ${String(selectedQuotation.event_end_date || '').slice(0, 10)}`,
      `Expected Pax: ${selectedQuotation.expected_pax || '-'}`,
      `Slot: ${String(selectedQuotation.slot_code || 'FULL_DAY').replace(/_/g, ' ')}`,
      (selectedQuotation.event_start_time && selectedQuotation.event_end_time)
        ? `Time: ${String(selectedQuotation.event_start_time).slice(0, 5)} - ${String(selectedQuotation.event_end_time).slice(0, 5)}`
        : null,
      `Estimated Total: ${Number(selectedQuotation.total_estimated || 0).toFixed(2)}`
    ].filter(Boolean);
    return lines.join('\n');
  }, [selectedQuotation]);

  const openWhatsAppShare = () => {
    if (!selectedQuotation) return setError('Select quotation first');
    const phone = String(selectedQuotation.customer_phone || '').replace(/\D/g, '');
    const base = phone ? `https://wa.me/${phone}` : 'https://wa.me/';
    window.open(`${base}?text=${encodeURIComponent(shareQuoteText)}`, '_blank');
  };

  const openEmailShare = () => {
    if (!selectedQuotation) return setError('Select quotation first');
    const email = encodeURIComponent(String(selectedQuotation.customer_email || '').trim());
    const subject = encodeURIComponent(`Banquet Quotation ${selectedQuotation.quotation_no}`);
    const body = encodeURIComponent(`${shareQuoteText}\n\nRegards,\nBanquet Team`);
    window.location.href = `mailto:${email}?subject=${subject}&body=${body}`;
  };

  const openPrintableQuote = () => {
    if (!selectedQuotation) return setError('Select quotation first');
    const itemRows = quotationItems.length
      ? quotationItems.map((it, idx) => `<tr><td style="padding:8px;border:1px solid #dcdde1;">${idx + 1}</td><td style="padding:8px;border:1px solid #dcdde1;">${it.item_type || '-'}</td><td style="padding:8px;border:1px solid #dcdde1;">${it.description || '-'}</td><td style="padding:8px;border:1px solid #dcdde1;text-align:right;">${Number(it.amount || 0).toFixed(2)}</td></tr>`).join('')
      : '<tr><td colspan="4" style="padding:8px;border:1px solid #dcdde1;">No items</td></tr>';
    const html = `
      <html>
      <head><title>Quotation ${selectedQuotation.quotation_no}</title></head>
      <body style="font-family:Segoe UI,Arial,sans-serif;padding:20px;color:#1f2937;">
        <div style="max-width:850px;margin:0 auto;border:1px solid #dcdde1;border-radius:10px;overflow:hidden;">
          <div style="background:linear-gradient(135deg,var(--theme-primary-dark),var(--theme-primary));color:#fff;padding:16px 18px;">
            <h2 style="margin:0;">Banquet Quotation</h2>
            <div style="opacity:0.9;">${selectedQuotation.quotation_no}</div>
          </div>
          <div style="padding:16px 18px;">
            <table style="width:100%;border-collapse:collapse;margin-bottom:14px;">
              <tr><td><b>Customer</b></td><td>${selectedQuotation.customer_name || '-'}</td><td><b>Phone</b></td><td>${selectedQuotation.customer_phone || '-'}</td></tr>
              <tr><td><b>Email</b></td><td>${selectedQuotation.customer_email || '-'}</td><td><b>Expected Pax</b></td><td>${selectedQuotation.expected_pax || '-'}</td></tr>
              <tr><td><b>Event Dates</b></td><td colspan="3">${String(selectedQuotation.event_start_date || '').slice(0, 10)} to ${String(selectedQuotation.event_end_date || '').slice(0, 10)}</td></tr>
              <tr><td><b>Slot</b></td><td>${String(selectedQuotation.slot_code || 'FULL_DAY').replace(/_/g, ' ')}</td><td><b>Time</b></td><td>${selectedQuotation.event_start_time && selectedQuotation.event_end_time ? `${String(selectedQuotation.event_start_time).slice(0, 5)} - ${String(selectedQuotation.event_end_time).slice(0, 5)}` : '-'}</td></tr>
            </table>
            <table style="width:100%;border-collapse:collapse;">
              <thead>
                <tr style="background:var(--theme-primary-dark);color:#fff;">
                  <th style="padding:8px;border:1px solid #dcdde1;text-align:left;">#</th>
                  <th style="padding:8px;border:1px solid #dcdde1;text-align:left;">Type</th>
                  <th style="padding:8px;border:1px solid #dcdde1;text-align:left;">Description</th>
                  <th style="padding:8px;border:1px solid #dcdde1;text-align:right;">Amount</th>
                </tr>
              </thead>
              <tbody>${itemRows}</tbody>
              <tfoot>
                <tr>
                  <td colspan="3" style="padding:10px;border:1px solid #dcdde1;text-align:right;"><b>Total Estimated</b></td>
                  <td style="padding:10px;border:1px solid #dcdde1;text-align:right;"><b>${Number(selectedQuotation.total_estimated || 0).toFixed(2)}</b></td>
                </tr>
              </tfoot>
            </table>
          </div>
        </div>
      </body></html>
    `;
    const w = window.open('', '_blank', 'width=980,height=780');
    if (!w) return;
    w.document.open();
    w.document.write(html);
    w.document.close();
  };
  const isSingleDayForm = Boolean(form.event_start_date) && Boolean(form.event_end_date) && String(form.event_start_date) === String(form.event_end_date);
  const quotationEditable = String(selectedQuotation?.status || '').toUpperCase() === 'DRAFT';
  const draftItemsTotal = useMemo(
    () => draftItems.reduce((sum, item) => sum + Number(item?.amount || 0), 0),
    [draftItems]
  );
  const itemsTotal = useMemo(
    () => quotationItems.reduce((sum, item) => sum + Number(item?.amount || 0), 0),
    [quotationItems]
  );

  const saveDraftItem = () => {
    setError('');
    const description = String(draftItemForm.description || '').trim();
    const amount = Number(draftItemForm.amount || 0);
    if (!description) return setError('Draft item description is required');
    if (!Number.isFinite(amount) || amount < 0) return setError('Draft item amount must be non-negative');
    const payload = {
      item_type: draftItemForm.item_type || 'SERVICE',
      description,
      amount
    };
    if (draftItemEditIndex >= 0) {
      setDraftItems((prev) => prev.map((row, idx) => (idx === draftItemEditIndex ? payload : row)));
    } else {
      setDraftItems((prev) => [...prev, payload]);
    }
    resetDraftItemForm();
  };

  const editDraftItem = (index) => {
    const row = draftItems[index];
    if (!row) return;
    setDraftItemEditIndex(index);
    setDraftItemForm({
      item_type: row.item_type || serviceModes[0]?.service_mode_code || 'SERVICE',
      description: row.description || '',
      amount: String(row.amount ?? '')
    });
  };

  const removeDraftItem = (index) => {
    setDraftItems((prev) => prev.filter((_, idx) => idx !== index));
    if (draftItemEditIndex === index) resetDraftItemForm();
  };

  const saveItem = async () => {
    setMessage('');
    setError('');
    if (!selectedQuotationId) return setError('Select quotation first for item addition');
    if (!quotationEditable) return setError('Only DRAFT quotations allow item changes');
    if (!String(itemForm.description || '').trim()) return setError('Item description is required');
    const amount = Number(itemForm.amount || 0);
    if (!Number.isFinite(amount) || amount < 0) return setError('Item amount must be non-negative');
    const isEdit = Boolean(editingItemId);
    const url = isEdit
      ? `/api/v1/banquet/quotations/${selectedQuotationId}/items/${editingItemId}`
      : `/api/v1/banquet/quotations/${selectedQuotationId}/items`;
    const res = await fetch(url, {
      method: isEdit ? 'PUT' : 'POST',
      headers: { ...headers, 'Content-Type': 'application/json' },
      body: JSON.stringify({
        item_type: itemForm.item_type || 'SERVICE',
        description: itemForm.description,
        amount
      })
    });
    const { error: apiError } = await parseJson(res);
    if (!res.ok) return setError(apiError || (isEdit ? 'Failed to update item' : 'Failed to add item'));
    setMessage(isEdit ? 'Quotation item updated' : 'Quotation item added');
    resetItemForm();
    const latestQuotes = await loadQuotations();
    const updated = (latestQuotes || []).find((q) => String(q.quotation_id) === String(selectedQuotationId));
    if (updated) setForm((prev) => ({ ...prev, total_estimated: String(updated.total_estimated ?? '') }));
    await loadQuotationItems(selectedQuotationId);
  };

  const editItem = (item) => {
    if (!quotationEditable) return;
    setEditingItemId(String(item.item_id));
    setItemForm({
      item_type: item.item_type || 'SERVICE',
      description: item.description || '',
      amount: String(item.amount ?? '')
    });
  };

  const deleteItem = async (itemId) => {
    setMessage('');
    setError('');
    if (!selectedQuotationId) return;
    if (!quotationEditable) return setError('Only DRAFT quotations allow item changes');
    const res = await fetch(`/api/v1/banquet/quotations/${selectedQuotationId}/items/${itemId}`, {
      method: 'DELETE',
      headers
    });
    const { error: apiError } = await parseJson(res);
    if (!res.ok) return setError(apiError || 'Failed to delete item');
    setMessage('Quotation item deleted');
    const latestQuotes = await loadQuotations();
    const updated = (latestQuotes || []).find((q) => String(q.quotation_id) === String(selectedQuotationId));
    if (updated) setForm((prev) => ({ ...prev, total_estimated: String(updated.total_estimated ?? '') }));
    await loadQuotationItems(selectedQuotationId);
  };

  const approveQuotation = async () => {
    setMessage('');
    setError('');
    if (!selectedQuotationId || !selectedHallId || !selectedLayoutId) {
      setError('Select quotation, hall and layout before approval');
      return;
    }
    try {
      const res = await fetch(`/api/v1/banquet/quotations/${selectedQuotationId}/approve`, {
        method: 'POST',
        headers: { ...headers, 'Content-Type': 'application/json' },
        body: JSON.stringify({
          hall_id: Number(selectedHallId),
          layout_id: Number(selectedLayoutId),
          event_name: form.event_name || null,
          advance_required: form.advance_required ? Number(form.advance_required) : null
        })
      });
      const { data, error: apiError } = await parseJson(res);
      if (!res.ok) throw new Error(apiError || 'Approval failed');
      setMessage(data?.message || 'Quotation approved');
      await loadQuotations();
    } catch (err) {
      setError(err?.message || 'Failed to approve quotation');
    }
  };

  const iconBtnStyle = {
    width: '40px',
    height: '40px',
    padding: 0,
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center'
  };

  return (
    <div className="page-shell">
      <h2 style={{ color: 'var(--theme-primary)' }}>Banquet - Quotations</h2>
      <BanquetHelpPanel visible={showHelp} onOpenFaq={onOpenFaq} />
      {!chainId || !propertyId ? <div className="error-banner">Select property first.</div> : null}
      {error ? <div className="error-banner">{error}</div> : null}
      {message ? <div className="success-banner">{message}</div> : null}

      <div className="filter-bar">
        <div style={{ fontWeight: 700, marginBottom: '0.55rem' }}>1. Quotation Details</div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: '0.6rem' }}>
          <input placeholder="Customer name" value={form.customer_name} onChange={(e) => setForm({ ...form, customer_name: e.target.value })} />
          <input placeholder="Customer phone" value={form.customer_phone} onChange={(e) => setForm({ ...form, customer_phone: e.target.value })} />
          <input placeholder="Customer email (optional)" value={form.customer_email} onChange={(e) => setForm({ ...form, customer_email: e.target.value })} />
          <input placeholder="Expected pax" type="number" value={form.expected_pax} onChange={(e) => setForm({ ...form, expected_pax: e.target.value })} />
          <input
            type="date"
            min={todayLocal}
            value={form.event_start_date}
            onChange={(e) => setForm({ ...form, event_start_date: e.target.value, event_end_date: e.target.value })}
          />
          <input
            type="date"
            min={form.event_start_date || todayLocal}
            value={form.event_end_date}
            onChange={(e) => setForm({ ...form, event_end_date: e.target.value })}
          />
          <select value={form.slot_code || 'FULL_DAY'} onChange={(e) => setForm({ ...form, slot_code: e.target.value })} disabled={!isSingleDayForm}>
            {(eventSlots.length ? eventSlots : [{ slot_code: 'FULL_DAY', slot_name: 'Full Day' }, { slot_code: 'LUNCH', slot_name: 'Lunch' }, { slot_code: 'DINNER', slot_name: 'Dinner' }, { slot_code: 'CUSTOM', slot_name: 'Specific Time' }]).map((s) => (
              <option key={s.slot_code} value={s.slot_code}>{s.slot_name || s.slot_code}</option>
            ))}
          </select>
          {isSingleDayForm && String(form.slot_code || '').toUpperCase() === 'CUSTOM' ? (
            <>
              <input type="time" value={form.event_start_time || ''} onChange={(e) => setForm({ ...form, event_start_time: e.target.value })} />
              <input type="time" value={form.event_end_time || ''} onChange={(e) => setForm({ ...form, event_end_time: e.target.value })} />
            </>
          ) : null}
          <input
            placeholder="Total estimated (auto)"
            type="number"
            step="0.01"
            readOnly
            value={editingQuotationId ? form.total_estimated : draftItemsTotal.toFixed(2)}
          />
          <button onClick={saveQuotation} disabled={loading || (!editingQuotationId && !draftItems.length)}>{editingQuotationId ? 'Update Quotation' : 'Create Quotation'}</button>
          {editingQuotationId ? <button onClick={resetForm} disabled={loading}>Cancel Edit</button> : null}
        </div>
      </div>

      {!editingQuotationId ? (
        <div className="filter-bar">
          <div style={{ fontWeight: 700, marginBottom: '0.55rem' }}>Create Quotation - Add Item Lines First</div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '0.6rem', marginBottom: '0.7rem' }}>
            <select value={draftItemForm.item_type} onChange={(e) => setDraftItemForm({ ...draftItemForm, item_type: e.target.value })}>
              {serviceModes.map((m) => <option key={m.service_mode_id} value={m.service_mode_code}>{m.service_mode_name}</option>)}
              {!serviceModes.length ? <option value="SERVICE">SERVICE</option> : null}
            </select>
            <input placeholder="Description" value={draftItemForm.description} onChange={(e) => setDraftItemForm({ ...draftItemForm, description: e.target.value })} />
            <input placeholder="Amount" type="number" step="0.01" min="0" value={draftItemForm.amount} onChange={(e) => setDraftItemForm({ ...draftItemForm, amount: e.target.value })} />
            <button onClick={saveDraftItem}>{draftItemEditIndex >= 0 ? 'Update Line' : 'Add Line'}</button>
            {draftItemEditIndex >= 0 ? <button onClick={resetDraftItemForm}>Cancel Line Edit</button> : null}
          </div>
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr>
                  <th style={{ textAlign: 'left' }}>Type</th>
                  <th style={{ textAlign: 'left' }}>Description</th>
                  <th style={{ textAlign: 'right' }}>Amount</th>
                  <th style={{ textAlign: 'left' }}>Action</th>
                </tr>
              </thead>
              <tbody>
                {draftItems.map((item, idx) => (
                  <tr key={`${item.item_type}-${idx}`}>
                    <td>{item.item_type}</td>
                    <td>{item.description}</td>
                    <td style={{ textAlign: 'right' }}>{Number(item.amount || 0).toFixed(2)}</td>
                    <td>
                      <button onClick={() => editDraftItem(idx)}>Edit</button>
                      <button onClick={() => removeDraftItem(idx)} style={{ marginLeft: '0.4rem' }}>Delete</button>
                    </td>
                  </tr>
                ))}
                {!draftItems.length ? <tr><td colSpan={4}>No lines added yet.</td></tr> : null}
              </tbody>
            </table>
          </div>
          <div style={{ marginTop: '0.6rem', fontWeight: 600 }}>Draft Total: {draftItemsTotal.toFixed(2)}</div>
        </div>
      ) : null}

      <div className="filter-bar">
        <div style={{ fontWeight: 700, marginBottom: '0.55rem' }}>2. Quotation Items</div>
        {!selectedQuotationId ? <div>Select quotation to manage items.</div> : null}
        {selectedQuotationId ? (
          <>
            <div style={{ marginBottom: '0.55rem', color: '#334155' }}>
              Status: {selectedQuotation?.status || '-'} | Items Total: {itemsTotal.toFixed(2)} | Quotation Estimate (Auto): {Number(selectedQuotation?.total_estimated || 0).toFixed(2)}
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '0.6rem', marginBottom: '0.7rem' }}>
              <select value={itemForm.item_type} onChange={(e) => setItemForm({ ...itemForm, item_type: e.target.value })} disabled={!quotationEditable}>
                {serviceModes.map((m) => <option key={m.service_mode_id} value={m.service_mode_code}>{m.service_mode_name}</option>)}
                {!serviceModes.length ? <option value="SERVICE">SERVICE</option> : null}
              </select>
              <input placeholder="Description" value={itemForm.description} onChange={(e) => setItemForm({ ...itemForm, description: e.target.value })} disabled={!quotationEditable} />
              <input placeholder="Amount" type="number" step="0.01" min="0" value={itemForm.amount} onChange={(e) => setItemForm({ ...itemForm, amount: e.target.value })} disabled={!quotationEditable} />
              <button onClick={saveItem} disabled={!quotationEditable}>{editingItemId ? 'Update Item' : 'Add Item'}</button>
              {editingItemId ? <button onClick={resetItemForm} disabled={!quotationEditable}>Cancel Item Edit</button> : null}
            </div>
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr>
                    <th style={{ textAlign: 'left' }}>Type</th>
                    <th style={{ textAlign: 'left' }}>Description</th>
                    <th style={{ textAlign: 'right' }}>Amount</th>
                    <th style={{ textAlign: 'left' }}>Action</th>
                  </tr>
                </thead>
                <tbody>
                  {quotationItems.map((item) => (
                    <tr key={item.item_id}>
                      <td>{item.item_type || '-'}</td>
                      <td>{item.description || '-'}</td>
                      <td style={{ textAlign: 'right' }}>{Number(item.amount || 0).toFixed(2)}</td>
                      <td>
                        <button onClick={() => editItem(item)} disabled={!quotationEditable}>Edit</button>
                        <button onClick={() => deleteItem(item.item_id)} disabled={!quotationEditable} style={{ marginLeft: '0.4rem' }}>Delete</button>
                      </td>
                    </tr>
                  ))}
                  {!quotationItems.length ? <tr><td colSpan={4}>No items added.</td></tr> : null}
                </tbody>
              </table>
            </div>
          </>
        ) : null}
      </div>

      <div className="filter-bar">
        <div style={{ fontWeight: 700, marginBottom: '0.55rem' }}>3. Approve Quotation</div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '0.6rem' }}>
          <select value={selectedQuotationId} onChange={(e) => setSelectedQuotationId(e.target.value)}>
            <option value="">Select quotation</option>
            {rows.map((r) => <option key={r.quotation_id} value={r.quotation_id}>{r.quotation_no} - {r.customer_name || 'Walk-in'}</option>)}
          </select>
          <select value={selectedHallId} onChange={(e) => setSelectedHallId(e.target.value)}>
            <option value="">Select hall</option>
            {halls.map((h) => <option key={h.hall_id} value={h.hall_id}>{h.hall_name}</option>)}
          </select>
          <select value={selectedLayoutId} onChange={(e) => setSelectedLayoutId(e.target.value)}>
            <option value="">Select layout</option>
            {layouts.map((l) => <option key={l.layout_id} value={l.layout_id}>{l.layout_name} (Cap {l.layout_capacity})</option>)}
          </select>
          <input placeholder="Event name (optional)" value={form.event_name} onChange={(e) => setForm({ ...form, event_name: e.target.value })} />
          <input placeholder="Advance required (optional)" type="number" step="0.01" value={form.advance_required} onChange={(e) => setForm({ ...form, advance_required: e.target.value })} />
          <button onClick={approveQuotation} disabled={loading}>Approve & Convert</button>
          <button onClick={openPrintableQuote}>View Quotation</button>
          <button onClick={openWhatsAppShare} title="Share WhatsApp" style={iconBtnStyle} aria-label="Share WhatsApp">
            <svg viewBox="0 0 24 24" width="18" height="18" fill="currentColor" aria-hidden="true">
              <path d="M12 2a10 10 0 0 0-8.6 15.1L2 22l5-1.3A10 10 0 1 0 12 2Zm0 18a8 8 0 0 1-4.1-1.1l-.3-.2-2.9.8.8-2.8-.2-.3A8 8 0 1 1 12 20Zm4.2-5.5c-.2-.1-1.2-.6-1.4-.6s-.3-.1-.5.1-.6.6-.7.7-.3.1-.5 0a6.5 6.5 0 0 1-1.9-1.2 7.3 7.3 0 0 1-1.4-1.8c-.1-.2 0-.3.1-.5l.3-.3.2-.3c.1-.1.1-.3 0-.4s-.5-1.3-.7-1.8c-.2-.5-.4-.4-.5-.4h-.5c-.2 0-.4.1-.6.3a2.5 2.5 0 0 0-.8 1.8c0 1 .8 2 1 2.3.1.1 1.7 2.6 4.2 3.6.6.3 1.1.4 1.5.5.6.2 1.2.2 1.7.1.5-.1 1.2-.5 1.3-1 .2-.5.2-1 .1-1.1-.1-.1-.2-.2-.4-.3Z" />
            </svg>
          </button>
          <button onClick={openEmailShare} title="Share Email" style={iconBtnStyle} aria-label="Share Email">
            <svg viewBox="0 0 24 24" width="18" height="18" fill="currentColor" aria-hidden="true">
              <path d="M4 5h16a2 2 0 0 1 2 2v10a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V7a2 2 0 0 1 2-2Zm0 2v.4l8 5 8-5V7H4Zm16 10V9.7l-7.5 4.7a1 1 0 0 1-1 0L4 9.7V17h16Z" />
            </svg>
          </button>
          <button onClick={reload} disabled={loading}>{loading ? 'Loading...' : 'Refresh'}</button>
        </div>
      </div>

      <div style={{ overflowX: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr>
              <th style={{ textAlign: 'left' }}>No</th>
              <th style={{ textAlign: 'left' }}>Customer</th>
              <th style={{ textAlign: 'left' }}>Customer Phone</th>
              <th style={{ textAlign: 'left' }}>Customer Email</th>
              <th style={{ textAlign: 'right' }}>Expected Pax</th>
              <th style={{ textAlign: 'left' }}>Slot</th>
              <th style={{ textAlign: 'left' }}>Dates</th>
              <th style={{ textAlign: 'right' }}>Estimate</th>
              <th style={{ textAlign: 'left' }}>Status</th>
              <th style={{ textAlign: 'left' }}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.quotation_id}>
                <td>{r.quotation_no}</td>
                <td>{r.customer_name || '-'}</td>
                <td>{r.customer_phone || '-'}</td>
                <td>{r.customer_email || '-'}</td>
                <td style={{ textAlign: 'right' }}>{r.expected_pax || '-'}</td>
                <td>{String(r.slot_code || 'FULL_DAY').replace(/_/g, ' ')}</td>
                <td>{String(r.event_start_date || '').slice(0, 10)} to {String(r.event_end_date || '').slice(0, 10)}</td>
                <td style={{ textAlign: 'right' }}>{Number(r.total_estimated || 0).toFixed(2)}</td>
                <td>{r.status}</td>
                <td>
                  <button onClick={() => editQuotation(r)} disabled={String(r.status || '').toUpperCase() !== 'DRAFT'}>Edit</button>
                </td>
              </tr>
            ))}
            {!rows.length ? <tr><td colSpan={10}>No quotations</td></tr> : null}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export default BanquetQuotation;
